import { z } from 'zod';
import { request, useMutation, useQueryClient } from '@applyboard/ui-utils';
import { VasErrorBody } from '../common/errors/VasError';
import { GIC_STATUS_VALUES } from '../pages/FintechServices/gic/constants';

export const GIC_APPLICATION_INTERNAL_UPDATE_V2_URL = (id: string) =>
  `/api/gic/v2/applications/${id}/internal`;

export const InternalUpdateGicApplicationV2RequestSchema = z.object({
  status: z.enum(GIC_STATUS_VALUES).optional(),
  recruitment_partner_id: z.string().optional(),
});

export type InternalUpdateGicApplicationV2Request = z.infer<
  typeof InternalUpdateGicApplicationV2RequestSchema
>;

export const GicApplicationResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.string(),
    attributes: z.object({
      status: z.enum(GIC_STATUS_VALUES),
      recruitment_partner_id: z.string().nullable(),
    }),
  }),
});

export type GicApplicationResponse = z.infer<typeof GicApplicationResponseSchema>;

export class GicApplicationError extends Error {
  public readonly errorBody: VasErrorBody;

  constructor(body: VasErrorBody, message: string) {
    super(message);
    this.errorBody = body;
  }
}

export function useGicApplicationInternalUpdate() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async (params: { id: string; body: InternalUpdateGicApplicationV2Request }) => {
      const { id, body } = params;
      const parsedRequest = InternalUpdateGicApplicationV2RequestSchema.parse(body);

      return request(GIC_APPLICATION_INTERNAL_UPDATE_V2_URL(id), {
        body: parsedRequest,
        method: 'PATCH',
        schema: GicApplicationResponseSchema,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new GicApplicationError(jsonMessage, 'GIC Application Internal Update V2 Failed');
        },
      });
    },
  );

  const gicApplicationInternalUpdate = (
    id: string,
    body: InternalUpdateGicApplicationV2Request,
    onSuccess?: (result: GicApplicationResponse) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(
      { id, body },
      {
        onSuccess: async (response: GicApplicationResponse) => {
          queryClient.invalidateQueries([`/api/gic_applications/${response?.data?.id}`]);
          onSuccess?.(response);
        },
        onError: (error: unknown) => onError?.(error),
      },
    );
  };

  return {
    gicApplicationInternalUpdate,
    isProcessingGicApplicationInternalUpdate: isLoading,
    gicApplicationInternalUpdateIsError: isError,
  };
}
